import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Input,
  Label,
  RadioButtonGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { PRESENCE_OPTIONS } from '../../../constants/wounds';
import type { UnderminingRow, UnderminingState } from '../assessmentFormTypes';

interface UnderminingSectionProps {
  state: UnderminingState;
  onChange: (update: Partial<UnderminingState>) => void;
}

const CLOCK_POSITIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const UnderminingSection: React.FC<UnderminingSectionProps> = ({
  state,
  onChange,
}) => {
  const updateRow = (idx: number, field: keyof UnderminingRow, value: number) =>
    onChange({
      rows: state.rows.map((row, i) =>
        i === idx ? { ...row, [field]: value } : row
      ),
    });

  return (
    <AccordionItem value='undermining' className='border rounded-md px-3'>
      <AccordionTrigger className='text-sm font-medium py-3'>
        Undermining
      </AccordionTrigger>
      <AccordionContent className='pb-3 space-y-3'>
        <RadioButtonGroup
          options={PRESENCE_OPTIONS.map(o => ({
            value: o.value,
            label: o.label,
          }))}
          value={state.value}
          onValueChange={v =>
            onChange({
              value: v,
              ...(v !== 'yes' && { rows: [] }),
              ...(v !== 'not_assessed' && { notAssessedReason: '' }),
            })
          }
        />
        {state.value === 'not_assessed' && (
          <Input
            placeholder='Reason not assessed...'
            value={state.notAssessedReason}
            onChange={e => onChange({ notAssessedReason: e.target.value })}
            inputSize='sm'
          />
        )}
        {state.value === 'yes' && (
          <div className='space-y-2'>
            {state.rows.map((row, idx) => (
              <div key={idx} className='flex items-center gap-2'>
                <div className='space-y-1 flex-1'>
                  <Label className='text-xs'>From</Label>
                  <Select
                    value={String(row.from_direction)}
                    onValueChange={v =>
                      updateRow(idx, 'from_direction', parseInt(v))
                    }
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLOCK_POSITIONS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label} o&apos;clock
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-1 flex-1'>
                  <Label className='text-xs'>To</Label>
                  <Select
                    value={String(row.to_direction)}
                    onValueChange={v =>
                      updateRow(idx, 'to_direction', parseInt(v))
                    }
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLOCK_POSITIONS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label} o&apos;clock
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-1 flex-1'>
                  <Label className='text-xs'>Depth (cm)</Label>
                  <Input
                    type='number'
                    step='0.1'
                    min='0'
                    value={row.depth_cm}
                    onChange={e =>
                      updateRow(
                        idx,
                        'depth_cm',
                        parseFloat(e.target.value) || 0
                      )
                    }
                    inputSize='sm'
                  />
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  className='mt-5 h-8 w-8 p-0'
                  onClick={() =>
                    onChange({ rows: state.rows.filter((_, i) => i !== idx) })
                  }
                >
                  <Trash2 className='h-4 w-4 text-destructive' />
                </Button>
              </div>
            ))}
            <Button
              variant='outline'
              size='sm'
              onClick={() =>
                onChange({
                  rows: [
                    ...state.rows,
                    { from_direction: 12, to_direction: 6, depth_cm: 0 },
                  ],
                })
              }
            >
              <Plus className='h-4 w-4 mr-1' />
              Add entry
            </Button>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export default UnderminingSection;
